import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Reklaim deltas vs the v4 shadcn registry (preserved on REK-1563 Stack 2 refresh):
 *
 * Variant scale (8 vs v4's 6):
 * - Reklaim adds `inverted` (light bg for dark surfaces) and `primary` (the
 *   brand-yellow CTA -- bg-primary text-primary-foreground). v4 has neither.
 * - Reklaim's `default` is dark-on-light (`bg-foreground text-background`) which
 *   reads as "authoritative neutral action" -- v4's `default` is brand-primary,
 *   which would map to the yellow CTA in our token system and clash with the
 *   neutral-action semantic our pages assume.
 *
 * Size scale (5 vs v4's 4):
 * - Reklaim's sizes are h-9 (sm) / h-11 (default) / h-12 (lg) / h-14 (xl) / h-10 (icon).
 *   v4 ships h-8 / h-9 / h-10 / h-10. We need the h-11 default + h-14 xl for the
 *   marketing CTA / hero buttons (e.g., 'Sign Up Now' on the homepage); the xl
 *   variant in particular has no v4 equivalent.
 *
 * Shape:
 * - Reklaim's base ring is `rounded-full` for pill-shape buttons. v4 uses
 *   `rounded-md`. This is a brand visual identifier, not a stylistic preference.
 *
 * Do NOT replace this file with `npx shadcn add button --overwrite` -- it would
 * lose 2 variants, 1 size, the pill shape, and the dark-default semantic, and
 * break dozens of call sites across auth + marketing + dashboard surfaces.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 rounded-full",
  {
    variants: {
      variant: {
        // Primary action: Dark background
        default: "bg-foreground text-background hover:bg-foreground/90",
        // Destructive
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // Outlined: Subtle border
        outline: "border border-border bg-transparent hover:bg-muted",
        // Secondary: Muted
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        // Ghost: Text only with hover
        ghost: "hover:bg-muted hover:text-foreground",
        // Link style
        link: "text-foreground underline-offset-4 hover:underline",
        // Inverted: For dark backgrounds
        inverted: "bg-background text-foreground hover:bg-background/90",
        // Primary yellow
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
      },
      size: {
        default: "h-11 px-6 text-sm",
        sm: "h-9 px-5 text-xs",
        lg: "h-12 px-8 text-base",
        xl: "h-14 px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
